// Componente de búsqueda global - Global search component
// Input de búsqueda con icono de lupa y atajo de teclado CTRL+K
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@shared/components/ui/input';
import { useIsMobile } from '@shared/hooks/useMobile';

import { GlobalSearchModal } from './GlobalSearchModal';
import { GlobalSearchModalMobile } from './GlobalSearchModalMobile';

interface GlobalSearchProps {
  onSearch?: (value: string) => void;
  className?: string;
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({ onSearch, className }) => {
  const { t } = useTranslation('menu');
  const isMobile = useIsMobile();
  const [searchValue, setSearchValue] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Detectar si es Mac - Detect if it's Mac
  const isMac =
    typeof navigator !== 'undefined' &&
    (navigator.platform.toUpperCase().indexOf('MAC') >= 0 || navigator.userAgent.toUpperCase().indexOf('MAC') >= 0);
  const shortcutKey = isMac ? 'CMD' : 'CTRL';

  // Manejar atajo de teclado CTRL+K o CMD+K - Handle keyboard shortcut CTRL+K or CMD+K
  // Solo en desktop, no en mobile - Only on desktop, not on mobile
  useEffect(() => {
    if (isMobile) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Verificar si se presionó CTRL+K (Windows/Linux) o CMD+K (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsModalOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobile]);

  // Abrir modal cuando el input tiene focus - Open modal when input has focus
  const handleFocus = () => {
    setIsModalOpen(true);
  };

  // Manejar cambio en el input - Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    onSearch?.(value);

    // Abrir modal si hay al menos 2 caracteres - Open modal if at least 2 characters
    if (value.trim().length >= 2) {
      setIsModalOpen(true);
    } else {
      setIsModalOpen(false);
    }
  };

  return (
    <>
      <div
        className={`relative flex items-center gap-2 px-3 py-2 rounded-md border border-card-border bg-card-bg mb-[-10px] ${className}`}
        data-testid="global-search-container"
      >
        {/* Icono de lupa - Search icon */}
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className="w-3.5 h-3.5 text-icons flex-shrink-0"
          data-testid="global-search-icon"
        />

        {/* Input de búsqueda - Search input */}
        <Input
          ref={inputRef}
          type="text"
          placeholder={t('global_search_placeholder')}
          value={searchValue}
          onChange={handleChange}
          onFocus={handleFocus}
          className="flex-1 border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:outline-none h-auto py-0 text-sm !shadow-none"
          data-testid="global-search-input"
        />

        {/* Atajo de teclado - Keyboard shortcut (solo en desktop) */}
        {!isMobile && (
          <div
            className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-card-border bg-card-bg text-xs text-complementary-text flex-shrink-0"
            data-testid="global-search-shortcut"
          >
            <span className="text-[10px]">{shortcutKey}</span>
            <span className="text-complementary-text">+</span>
            <span className="text-[10px]">K</span>
          </div>
        )}
      </div>

      {/* Modal de búsqueda - Search modal (desktop o mobile según el dispositivo) */}
      {isMobile ? (
        <GlobalSearchModalMobile
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          searchQuery={searchValue}
          onSearchQueryChange={setSearchValue}
        />
      ) : (
        <GlobalSearchModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          searchQuery={searchValue}
          onSearchQueryChange={setSearchValue}
        />
      )}
    </>
  );
};
